import { useEffect, useRef, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from '@/lib/cn';

type CopyState = 'idle' | 'copied' | 'failed';

const FEEDBACK_MS = 2000;

/**
 * Copy one value — a transaction hash, an address — to the clipboard.
 *
 * `navigator.clipboard` is unavailable outside a secure context and can be
 * refused by permission policy, so failure is a real state and is shown rather
 * than swallowed: a trader who thinks they copied a hash and pastes the
 * previous one has lost the reference to their own payment.
 */
export function CopyButton({
  value,
  label,
  className,
}: {
  value: string;
  /** Names the thing being copied, e.g. «хеш транзакции». */
  label: string;
  className?: string;
}) {
  const [state, setState] = useState<CopyState>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const flash = (next: CopyState): void => {
    setState(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setState('idle'), FEEDBACK_MS);
  };

  const copy = async (): Promise<void> => {
    try {
      if (!navigator.clipboard) throw new Error('clipboard unavailable');
      await navigator.clipboard.writeText(value);
      flash('copied');
    } catch {
      flash('failed');
    }
  };

  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      <button
        type="button"
        onClick={() => void copy()}
        aria-label={`Скопировать ${label}`}
        className={cn(
          // A real 44px box rather than an expanded pseudo-element: this sits
          // inside a dense row next to other controls, and an overlay would
          // steal their taps.
          'focus-ring flex h-11 w-11 shrink-0 items-center justify-center rounded-sm',
          'text-fg-muted transition-colors duration-fast',
          'hover:bg-sunken hover:text-fg lg:h-8 lg:w-8',
        )}
      >
        {state === 'copied' ? (
          <Check size={15} strokeWidth={1.5} aria-hidden="true" />
        ) : (
          <Copy size={15} strokeWidth={1.5} aria-hidden="true" />
        )}
      </button>

      {/* Announced once, then removed — not a permanent second label. */}
      <span role="status" aria-live="polite" className="text-2xs text-fg-muted">
        {state === 'copied' ? 'Скопировано' : state === 'failed' ? 'Не удалось скопировать' : ''}
      </span>
    </span>
  );
}
